/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  completeUrl,
  getFormatTypeFromKnowledgeFilter,
  type FilterKnowledgeType,
  WEBSITE_CRAWL_JOB_QUERY_KEY,
  useDataModalWithCoze,
  WEBSITE_FORMAT_TYPE,
  WEBSITE_URL_QUERY_KEY,
} from '@coze-data/utils';
import { useDataNavigate } from '@coze-data/knowledge-stores';
import { UnitType } from '@coze-data/knowledge-resource-processor-core';
import {
  CozeKnowledgeAddTypeContent,
  type CozeKnowledgeAddTypeContentFormData,
} from '@coze-data/knowledge-modal-base/create-knowledge-modal-v2';
import { KnowledgeE2e } from '@coze-data/e2e';
import { I18n } from '@coze-arch/i18n';
import { Button, Form, LoadingButton } from '@coze-arch/coze-design';
import { useSpaceStore } from '@coze-arch/bot-studio-store';
import { FormatType } from '@coze-arch/bot-api/memory';
import { KnowledgeApi, websiteKnowledgeApi } from '@coze-arch/bot-api';

import styles from './index.module.less';

export interface UseCreateKnowledgeModalParams {
  projectID?: string;
  onFinish?: (datasetId: string, type: UnitType, shouldUpload: boolean) => void;
  beforeCreate?: (shouldUpload: boolean) => void;
  onCreated?: (datasetId: string) => void | Promise<void>;
}

const CreateKnowledgeModalFooter = ({
  close,
  currentFormatType,
  handleWebsiteCreate,
  handleCreate,
}: {
  close: () => void;
  currentFormatType: number;
  handleWebsiteCreate: () => Promise<void>;
  handleCreate: (shouldUpload: boolean) => Promise<void>;
}) => (
  <div className="flex w-full justify-end">
    <Button color="primary" onClick={close}>
      {I18n.t('cancel')}
    </Button>
    {currentFormatType === WEBSITE_FORMAT_TYPE ? (
      <LoadingButton color="primary" onClick={handleWebsiteCreate}>
        {I18n.t('knowledge_website_discover_pages')}
      </LoadingButton>
    ) : (
      <>
        <LoadingButton color="primary" onClick={() => void handleCreate(false)}>
          {I18n.t('kl_write_108')}
        </LoadingButton>
        <LoadingButton
          data-testid={KnowledgeE2e.CreateKnowledgeModalSubmitAndImportButton}
          color="primary"
          onClick={() => void handleCreate(true)}
        >
          {I18n.t('kl_write_109')}
        </LoadingButton>
      </>
    )}
  </div>
);

export const useCreateKnowledgeModalV2 = (
  params: UseCreateKnowledgeModalParams = {},
) => {
  const { onFinish, beforeCreate, onCreated, projectID } = params;
  const navigate = useNavigate();
  const routeParams = useParams<{ space_id?: string; bot_id?: string }>();
  const formRef = useRef<Form<CozeKnowledgeAddTypeContentFormData>>(null);
  // Use useState to ensure re-rendering
  const [currentFormatType, setCurrentFormatType] = useState<number>(
    FormatType.Text,
  );
  const [defaultFormatType, setDefaultFormatType] = useState<number>(
    FormatType.Text,
  );
  const spaceId = useSpaceStore(store => store.getSpaceId());
  const routeSpaceId =
    routeParams.space_id ||
    window.location.pathname.match(/\/space\/([^/]+)/)?.[1];

  const resourceNavigate = useDataNavigate();

  const [unitType, setUnitType] = useState<UnitType>(UnitType.TEXT_DOC);

  const createDataset = async () => {
    await formRef.current?.formApi.validate();
    const { dataset_id: datasetId } = await KnowledgeApi.CreateDataset({
      project_id: projectID || undefined,
      name: formRef.current?.formApi.getValue('name'),
      format_type: currentFormatType as FormatType,
      description: formRef.current?.formApi.getValue('description'),
      icon_uri: formRef.current?.formApi.getValue('icon_uri')?.[0].uid,
      space_id: spaceId || undefined,
    });
    return datasetId;
  };

  const navigateToWebsiteKnowledge = (
    datasetId: string,
    jobId: string,
    websiteURL: string,
  ) => {
    const query = Object.fromEntries(
      new URLSearchParams(window.location.search).entries(),
    );

    if (!query.biz && routeParams.bot_id) {
      query.biz = 'agentIDE';
    }
    if (!query.bot_id && routeParams.bot_id) {
      query.bot_id = routeParams.bot_id;
    }
    if (!query.page_mode && routeParams.bot_id) {
      query.page_mode = 'modal';
    }
    if (!query.action_type && routeParams.bot_id) {
      query.action_type = 'remove';
    }

    const nextQuery = {
      ...query,
      from: 'create',
      [WEBSITE_CRAWL_JOB_QUERY_KEY]: jobId,
      [WEBSITE_URL_QUERY_KEY]: websiteURL,
    };

    if (resourceNavigate.toResource) {
      resourceNavigate.toResource('knowledge', datasetId, nextQuery);
      return;
    }

    if (!routeSpaceId) {
      return;
    }

    navigate({
      pathname: `/space/${routeSpaceId}/knowledge/${datasetId}`,
      search: new URLSearchParams(nextQuery).toString(),
    });
  };

  const handleCreate = async (shouldUpload: boolean) => {
    beforeCreate?.(shouldUpload);
    const datasetId = await createDataset();
    if (datasetId) {
      await onCreated?.(datasetId);
    }
    close();
    if (onFinish) {
      onFinish(datasetId || '', unitType, shouldUpload);
    } else if (shouldUpload) {
      resourceNavigate.upload?.({ type: unitType });
    } else {
      resourceNavigate.toResource?.('knowledge', datasetId);
    }
  };

  const handleWebsiteCreate = async () => {
    await formRef.current?.formApi.validate();
    const datasetId = await createDataset();
    if (datasetId) {
      await onCreated?.(datasetId);
    }
    close();
    const websiteURL = completeUrl(
      formRef.current?.formApi.getValue('website_url') || '',
    );
    const resp = await websiteKnowledgeApi.createCrawlJob({
      dataset_id: datasetId || '',
      website_url: websiteURL,
    });
    if (datasetId && resp.job_id) {
      navigateToWebsiteKnowledge(datasetId, resp.job_id, websiteURL);
    }
  };

  const { open, close, modal } = useDataModalWithCoze({
    title: (
      <div data-testid={KnowledgeE2e.CreateKnowledgeModalTitle}>
        {I18n.t('datasets_model_create_title')}
      </div>
    ),
    centered: true,
    className: styles['create-knowledge-modal'],
    onCancel: () => {
      close();
    },
    footer: (
      <CreateKnowledgeModalFooter
        close={close}
        currentFormatType={currentFormatType}
        handleWebsiteCreate={handleWebsiteCreate}
        handleCreate={handleCreate}
      />
    ),
  });

  return {
    modal: modal(
      <Form<CozeKnowledgeAddTypeContentFormData>
        ref={formRef}
        showValidateIcon={false}
        className={styles['create-form']}
      >
        <CozeKnowledgeAddTypeContent
          onImportKnowledgeTypeChange={setUnitType}
          onSelectFormatTypeChange={setCurrentFormatType}
          defaultFormatType={defaultFormatType}
        />
      </Form>,
    ),
    open: (defaultType?: FilterKnowledgeType) => {
      const nextFormatType =
        getFormatTypeFromKnowledgeFilter(defaultType) ?? FormatType.Text;
      setCurrentFormatType(nextFormatType);
      setDefaultFormatType(nextFormatType);
      open();
    },
    close,
  };
};
